import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ScaleQueryType } from '../graphql/types/dashboard/ScaleDashboardTypes';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

/** 数据看板统一按美西时区统计与展示 */
export const DASHBOARD_PACIFIC_TZ = 'America/Los_Angeles';

/** 当前时刻的美西日期（用于「今日」「昨日」等快捷选项） */
export function dashboardNowInPacific(): Dayjs {
    return dayjs().tz(DASHBOARD_PACIFIC_TZ);
}

/** epoch 秒 -> 美西 YYYY-MM-DD HH:mm:ss，无值返回 '-' */
export function formatPacificTime(seconds?: number | null): string {
    if (!seconds) return '-';
    return dayjs.unix(seconds).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm:ss');
}

/** 美西「昨日」YYYY-MM-DD（页面默认「昨日」应对齐此值，与北京时间 16:00 前后美西日历切换一致） */
export function dashboardPacificYesterdayStr(): string {
    return dashboardNowInPacific().subtract(1, 'day').format('YYYY-MM-DD');
}

/** 美西「上周」完整周起止 */
export function dashboardPacificLastWeekRange(): { start: string; end: string } {
    const today = dashboardNowInPacific();
    const start = today.subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');
    const end = today.subtract(1, 'week').endOf('isoWeek').startOf('day').format('YYYY-MM-DD');
    return { start, end };
}

/** 美西「上月」完整月起止 */
export function dashboardPacificLastMonthRange(): { start: string; end: string } {
    const today = dashboardNowInPacific();
    const start = today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    const end = today.subtract(1, 'month').endOf('month').startOf('day').format('YYYY-MM-DD');
    return { start, end };
}

export type DashboardComparisonAvgLabel = string;

/**
 * 根据时间范围（美西口径）返回「均值列」标题。
 *
 * 单期场景该列实际写入的是「对比期值」（来自 data2），标题用固定文案而非具体日期：
 * - DAILY 单日（含预设「昨日」）→ 对比期 = 该日 T-7，标题 = `上周同日`
 * - WEEKLY 对齐到完整 ISO 周（周一~周日）→ 对比期 = 前一 ISO 周，标题 = `上一周`
 * - MONTHLY 对齐到完整自然月（1 日~月末）→ 对比期 = 前一自然月，标题 = `上一月`
 * - 今日实时 → 对比期 = T-7 同时间段，标题 = `上周同期`
 * - 其他（多日 / 跨周 / 跨月 / 半月等）→ 多期均值场景，标题 = `日均/周均/月均`
 */
export function resolveDashboardComparisonAvgLabel(
    queryType: ScaleQueryType,
    startDate: string,
    endDate: string,
    todayRealtime?: boolean
): DashboardComparisonAvgLabel {
    if (todayRealtime === true) {
        return '上周同期';
    }

    const start = dayjs.tz(startDate, DASHBOARD_PACIFIC_TZ);
    const end = dayjs.tz(endDate, DASHBOARD_PACIFIC_TZ);
    if (!start.isValid() || !end.isValid()) {
        return queryType === ScaleQueryType.DAILY ? '日均' : queryType === ScaleQueryType.WEEKLY ? '周均' : '月均';
    }

    if (queryType === ScaleQueryType.DAILY && start.isSame(end, 'day')) {
        return '上周同日';
    }

    if (
        queryType === ScaleQueryType.WEEKLY &&
        start.isoWeekday() === 1 &&
        end.isoWeekday() === 7 &&
        end.diff(start, 'day') === 6
    ) {
        return '上一周';
    }

    if (
        queryType === ScaleQueryType.MONTHLY &&
        start.isSame(end, 'month') &&
        start.date() === 1 &&
        end.isSame(end.endOf('month'), 'day')
    ) {
        return '上一月';
    }

    return queryType === ScaleQueryType.DAILY ? '日均' : queryType === ScaleQueryType.WEEKLY ? '周均' : '月均';
}

/** 多期均值列固定标签（后端聚合行也可能用这几个，需在 timePeriod 过滤里识别） */
const DASHBOARD_AVG_FIXED_LABELS = ['日均', '周均', '月均', '上周同日', '上周同期', '上一周', '上一月'] as const;

/**
 * 判断 timePeriod 字符串是否为「均值列标签」，用于把后端聚合行从真实日期列中过滤掉。
 * - 固定标签：日均 / 周均 / 月均 / 上周同期
 * - 动态对比期标签：单日 `YYYY-MM-DD`、周/月 `YYYY-MM-DD~YYYY-MM-DD`
 *   这两类格式只在前端 avgLabel 出现，后端不会返回，因此只判断固定标签即可。
 */
export function isDashboardAvgColumnLabel(period: string): boolean {
    return (DASHBOARD_AVG_FIXED_LABELS as readonly string[]).includes(period);
}

/** @deprecated 仅为向后兼容保留，请用 isDashboardAvgColumnLabel 判断。 */
export const DASHBOARD_AVG_COLUMN_LABELS = DASHBOARD_AVG_FIXED_LABELS;

/**
 * HUB sortingCode 在前端展示用的标签映射。
 * 数据库 sortingCode 存的是 'LA'，但 UI / 导出统一展示为 'LAX'。
 * 其它 HUB（EWR / ORD / ATL / ...）展示与 sortingCode 一致。
 * 子 HUB（LAXS / LAXSCBT / LAXSTEMU / LAXSFBT / EWRS）保持原值不变。
 */
export function formatHubDisplayLabel(sortingCode: string | null | undefined): string {
    const code = (sortingCode ?? '').trim();
    if (code === 'LA') return 'LAX';
    return code;
}
